import { type FC, type MouseEvent, useEffect, useCallback, useReducer } from 'react';
import { graphql } from 'react-relay/legacy';
import { useFragment, useRelayEnvironment } from 'react-relay';
import { useDispatch } from 'react-redux';
import classnames from 'classnames';
import SV from 'server-vars';
import { FormattedMessage, useIntl, defineMessages } from 'dibs-react-intl';
import { handleLocaleUrl } from 'dibs-intl/exports/urls';
import { type Locale, isLocale, LOCALE_US } from 'dibs-intl/exports/locales';
import { getLanguageOptions } from 'dibs-intl/exports/languages';
import getCurrencySymbol from 'dibs-currency-helpers/exports/getCurrencySymbol';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';
import {
    getUserSessionCountryCode,
    setUserGeoInfo,
} from 'dibs-regional-info/exports/regionalInfoHelpers';
import { forceSetUserRegionalInfo } from 'dibs-regional-info/exports/setUserRegionalInfo';

import { Button } from 'dibs-elements/exports/Button';
import { Link } from 'dibs-elements/exports/Link';
import { BasicSelect } from 'dibs-elements/exports/BasicSelect';
import { ModalContainer } from 'dibs-elements/exports/ModalContainer';
import { ModalBody } from 'dibs-elements/exports/ModalBody';
import { BarHeader } from 'dibs-elements/exports/BarHeader';
import { ModalCloseButton } from 'dibs-elements/exports/ModalCloseButton';
import World from 'dibs-icons/exports/legacy/World';

import { setLocalCurrency } from '../../utils/preferredCurrency';
import { actionCreators } from '../../actions';
import { changeDisplayPreferrencesMutation } from './changeDisplayPreferrencesMutation';
import { setLangCookie } from '../../mutations/setLangCookieMutation';
import { editAccountPartialShippingAddress } from '../../mutations/editAccountPartialShippingAddressMutation';
import { useCurrency } from '../CurrencyProvider/useCurrency';
import { trackNavigationClick } from '../../helpers/trackingHelpers';
import { type DisplaySettingsModal_viewer$key } from './__generated__/DisplaySettingsModal_viewer.graphql';
import { type DisplaySettingsModal_user$key } from './__generated__/DisplaySettingsModal_user.graphql';

import styles from './DisplaySettingsModal.scss';

type Props = {
    viewer: DisplaySettingsModal_viewer$key;
    user: DisplaySettingsModal_user$key | null | undefined;
    placement?: 'footer' | 'header' | 'mobileHeader' | 'mobileFooter';
};

type FormState = {
    country: string;
    currency: string;
    preferredLocale: Locale;
    isDirty: boolean;
    isOpen: boolean;
    isDisabled: boolean;
};

type FormAction =
    | { type: 'open' }
    | { type: 'close' }
    | { type: 'disable' }
    | { type: 'setCountry'; payload: { country: string; initialize?: boolean } }
    | { type: 'setPreferredLocale'; payload: { preferredLocale: Locale } }
    | { type: 'setCurrency'; payload: { currency: string; initialize?: boolean } };

function reducer(state: FormState, action: FormAction): FormState {
    switch (action.type) {
        case 'setCountry': {
            return {
                ...state,
                isDirty: !action.payload.initialize,
                country: action.payload.country,
            };
        }
        case 'setPreferredLocale': {
            return { ...state, isDirty: true, preferredLocale: action.payload.preferredLocale };
        }
        case 'setCurrency': {
            return {
                ...state,
                isDirty: !action.payload.initialize,
                currency: action.payload.currency,
            };
        }
        case 'open': {
            return { ...state, isOpen: true };
        }
        case 'close': {
            return { ...state, isOpen: false };
        }
        case 'disable': {
            return { ...state, isDisabled: true };
        }
        default:
            throw new Error('using wrong action type in DisplaySettingsModal');
    }
}

const messages = defineMessages({
    header: {
        id: 'dbl.Footer.DisplaySettingsModal.header',
        defaultMessage: 'Update Display Settings',
    },
    description: {
        id: 'dbl.Footer.DisplaySettingsModal.description',
        defaultMessage: 'Select your shipping destination, language and currency.',
    },
});

const DisplaySettingsModal: FC<Props> = ({
    viewer: viewerRef,
    user: userRef,
    placement = 'footer',
}) => {
    const environment = useRelayEnvironment();
    const viewer = useFragment(
        graphql`
            fragment DisplaySettingsModal_viewer on Viewer {
                currencies
                geography {
                    countries {
                        name
                        abbreviation
                    }
                }
                scandinavianCurrenciesEnabled: featureFlag(feature: "scandinavianCurrencies")
            }
        `,
        viewerRef
    );
    const user = useFragment(
        graphql`
            fragment DisplaySettingsModal_user on User {
                id
                serviceId
                profile {
                    currency
                }
                addressBook {
                    personalAddress {
                        country
                        displayCountry
                    }
                }
            }
        `,
        userRef
    );

    const intl = useIntl();
    const dispatch = useDispatch();

    const { currency: currentCurrency, showCurrency } = useCurrency();

    const currentCountry =
        user?.addressBook?.personalAddress?.country || getUserSessionCountryCode();

    const isInFooter = placement === 'footer';
    const isInHeader = placement === 'header';
    const isInMobileHeader = placement === 'mobileHeader';
    const isInMobileFooter = placement === 'mobileFooter';

    const [formState, formDispatch] = useReducer(reducer, {
        country: currentCountry,
        currency: currentCurrency,
        preferredLocale: intl.locale as Locale,
        isDirty: false,
        isOpen: false,
        isDisabled: false,
    });

    useEffect(() => {
        formDispatch({
            type: 'setCountry',
            payload: { initialize: true, country: currentCountry },
        });
    }, [currentCountry]);

    useEffect(() => {
        formDispatch({
            type: 'setCurrency',
            payload: { initialize: true, currency: currentCurrency },
        });
    }, [currentCurrency]);

    const handleModalClose = useCallback(() => {
        if (formState.isDisabled) {
            return;
        }
        formDispatch({ type: 'close' });
    }, [formState.isDisabled]);

    const onPreferrencesUpdateComplete = useCallback(
        (currency: string, locale: Locale) => {
            setLocalCurrency(currency);
            dispatch(actionCreators.populateCurrency(currency));

            const hasUrlParams = document.location.search !== '';
            const urlParams = new URLSearchParams(document.location.search);
            const hasCurrencyQp = urlParams.get('currency');

            if (hasCurrencyQp) {
                urlParams.set('currency', formState.currency);
            }

            let url = '';

            if (hasUrlParams) {
                url = document.location.pathname + '?' + urlParams.toString();
            } else {
                url = document.location.pathname;
            }

            window.location.replace(handleLocaleUrl(url, locale));
        },
        [dispatch, formState.currency]
    );

    const handleFormSubmit = useCallback(
        async (event: MouseEvent) => {
            formDispatch({ type: 'disable' });

            const hasCountryChanged = formState.country !== currentCountry;
            const hasCurrencyChanged = formState.currency !== currentCurrency;
            const hasLocaleChanged = formState.preferredLocale !== intl.locale;

            if (hasLocaleChanged) {
                trackNavigationClick(
                    {
                        label: `change language|${formState.preferredLocale} - intl`,
                        isInteractiveEvent: true,
                        isHeader: isInHeader || isInMobileHeader,
                    },
                    event
                );
                await setLangCookie(environment, {
                    locale: formState.preferredLocale,
                    cookieDomain: SV.get('cookieDomain'),
                });
            }

            if (hasCurrencyChanged) {
                trackNavigationClick(
                    {
                        label: `change currency|${formState.currency} - intl`,
                        isInteractiveEvent: true,
                        additional: { defaultCurrency: `${formState.currency} Selected` },
                        isHeader: isInHeader || isInMobileHeader,
                    },
                    event
                );
            }

            if (hasCountryChanged) {
                trackNavigationClick(
                    {
                        label: `change region|${formState.country} - intl`,
                        isInteractiveEvent: true,
                        isHeader: isInHeader || isInMobileHeader,
                    },
                    event
                );
                if (user?.serviceId) {
                    await editAccountPartialShippingAddress(environment, {
                        userId: user.serviceId,
                        address: { country: formState.country, zipCode: null },
                    });
                    await forceSetUserRegionalInfo({
                        relayEnv: environment,
                        cookieDomain: SV.get('cookieDomain'),
                        userId: user?.serviceId,
                        useLocalStorage: true,
                    });
                } else {
                    await setUserGeoInfo({
                        userGeoInfo: {
                            countryCode: formState.country,
                            zipCode: null,
                            incomeLevel: null,
                            regionsByZipCode: null,
                        },
                        environment,
                        useLocalStorage: true,
                    });
                }
            }

            if (user) {
                changeDisplayPreferrencesMutation({
                    environment,
                    user: user,
                    currency: hasCurrencyChanged ? formState.currency : null,
                    preferredLocale: hasLocaleChanged ? formState.preferredLocale : null,
                    onCompleted: () =>
                        onPreferrencesUpdateComplete(formState.currency, formState.preferredLocale),
                });
            } else {
                onPreferrencesUpdateComplete(formState.currency, formState.preferredLocale);
            }
        },
        [
            formState.country,
            formState.currency,
            formState.preferredLocale,
            currentCountry,
            currentCurrency,
            intl.locale,
            user,
            isInHeader,
            isInMobileHeader,
            environment,
            onPreferrencesUpdateComplete,
        ]
    );

    const languageOptions = getLanguageOptions();

    const currentLocaleLabel = isLocale(intl.locale)
        ? languageOptions.find(c => c.value === intl.locale)?.label
        : languageOptions.find(c => c.value === LOCALE_US)?.label;

    const countries = (viewer.geography?.countries || [])
        .map(country => {
            if (country?.name && country.abbreviation) {
                return {
                    value: country.abbreviation,
                    label: country.name,
                };
            }
            return null;
        })
        .filter(filterNulls);

    //Clean up after scandinavianCurrencies Feature Flag is turned off
    const activeCurrencies = viewer?.scandinavianCurrenciesEnabled
        ? viewer?.currencies
        : ['USD', 'GBP', 'EUR', 'CAD', 'AUD', 'CHF', 'MXN'];

    const currencies = (
        (activeCurrencies || []).map(currency => {
            const cuurrencySymbol = getCurrencySymbol(currency);
            return currency
                ? {
                      label: cuurrencySymbol ? `${cuurrencySymbol} ${currency}` : currency,
                      value: currency,
                  }
                : null;
        }) || []
    ).filter(filterNulls);

    const currentCurrencyLabel = currencies.find(c => c.value === currentCurrency)?.label;

    const countryDisplayLabel = countries.find(c => c.value === currentCountry)?.label;

    if (!showCurrency && (isInFooter || isInMobileFooter)) {
        return <div className={styles.placeholder} />;
    }

    const ctaClass = classnames({
        [styles.modalButton]: isInFooter || isInMobileFooter,
        [styles.headerButton]: isInHeader,
        [styles.mobileHeaderButton]: isInMobileHeader,
    });

    const ctaWrapperClass = classnames({
        [styles.modalButtonContainer]: isInFooter || isInMobileFooter,
        [styles.headerModalButtonContainer]: isInHeader,
    });

    const CtaComponent = isInHeader ? Link : Button;

    return (
        <div
            data-tn={`display-settings-${placement}`}
            className={isInHeader ? styles.headerContainer : ''}
        >
            <div className={ctaWrapperClass}>
                <CtaComponent
                    className={ctaClass}
                    onClick={event => {
                        formDispatch({ type: 'open' });
                        trackNavigationClick(
                            {
                                label: 'region selector modal entry - intl',
                                isHeader: isInHeader || isInMobileHeader,
                            },
                            event
                        );
                    }}
                    type="textCta"
                    size="small"
                    dataTn="display-settings-modal-cta"
                >
                    {isInHeader && <World className={styles.headerIcon} />}
                    {!isInHeader && (
                        <div className={styles.ctaContentContainer}>
                            <World className={styles.icon} />
                            {countryDisplayLabel && !isInMobileFooter && (
                                <span className={styles.labelSeparator}>
                                    {isInMobileHeader ? currentCountry : countryDisplayLabel}
                                </span>
                            )}
                            {!isInMobileHeader && (
                                <span className={styles.labelSeparator}>{currentLocaleLabel}</span>
                            )}
                            <span>{currentCurrencyLabel}</span>
                        </div>
                    )}
                </CtaComponent>
            </div>
            <ModalContainer
                isOpen={formState.isOpen}
                ariaLabel={intl.formatMessage(messages.header)}
                onClose={handleModalClose}
            >
                <ModalBody>
                    <BarHeader
                        className={styles.header}
                        title={intl.formatMessage(messages.header)}
                    />
                    <ModalCloseButton onClick={handleModalClose} />
                    <p className={styles.description}>{intl.formatMessage(messages.description)}</p>

                    <div className={styles.inputContainer}>
                        <BasicSelect
                            disabled={formState.isDisabled}
                            dataTn="display-settings-modal-country-select"
                            name="country-select"
                            onChange={event =>
                                formDispatch({
                                    type: 'setCountry',
                                    payload: { country: event.target.value },
                                })
                            }
                            value={formState.country}
                            options={countries}
                        />
                    </div>

                    <div className={styles.inputContainer}>
                        <BasicSelect
                            disabled={formState.isDisabled}
                            dataTn="display-settings-modal-locale-select"
                            name="locale-select"
                            onChange={event =>
                                formDispatch({
                                    type: 'setPreferredLocale',
                                    payload: { preferredLocale: event.target.value as Locale },
                                })
                            }
                            value={formState.preferredLocale}
                            options={languageOptions}
                        />
                    </div>
                    <div className={styles.inputContainer}>
                        <BasicSelect
                            disabled={formState.isDisabled}
                            dataTn="display-settings-modal-currency-select"
                            name="currency-select"
                            onChange={event =>
                                formDispatch({
                                    type: 'setCurrency',
                                    payload: {
                                        currency: event.target.value,
                                    },
                                })
                            }
                            options={currencies}
                            value={formState.currency}
                        />
                    </div>
                    <Button
                        fullWidth
                        dataTn="display-settings-modal-submit"
                        disabled={formState.isDisabled || !formState.isDirty}
                        onClick={handleFormSubmit}
                    >
                        <FormattedMessage
                            id="dbl.Footer.DisplaySettingsModal.submit"
                            defaultMessage="Submit"
                        />
                    </Button>
                </ModalBody>
            </ModalContainer>
        </div>
    );
};

export default DisplaySettingsModal;
